import type { Action, Resource, Role } from '@buildbee/contracts';

/**
 * What the Roles & permissions page shows: every resource:action the app checks, grouped the way
 * an owner thinks about the business, with labels written from the user's side. The engine decides
 * on any cell; this list only decides what can be seen and ticked.
 */

export type CatalogCell = { resource: Resource; action: Action; label: string };
export type CatalogItem = { resource: Resource; label: string; cells: CatalogCell[] };
export type CatalogGroup = { id: string; label: string; items: CatalogItem[] };

const VERBS: Record<Action, string> = {
  view: 'See',
  create: 'Add',
  update: 'Edit',
  delete: 'Delete',
  approve: 'Approve',
  export: 'Export',
  manage: 'Manage',
};

type ItemSpec = [resource: Resource, noun: string, actions: Action[], labels?: Partial<Record<Action, string>>];

function item([resource, noun, actions, labels]: ItemSpec): CatalogItem {
  const label = noun.charAt(0).toUpperCase() + noun.slice(1);
  return {
    resource,
    label,
    cells: actions.map((action) => ({ resource, action, label: labels?.[action] ?? `${VERBS[action]} ${noun}` })),
  };
}

const CRUD: Action[] = ['view', 'create', 'update', 'delete'];

const GROUPS: [id: string, label: string, items: ItemSpec[]][] = [
  [
    'crm',
    'Sales & CRM',
    [
      ['company', 'companies', ['view', 'create', 'update', 'delete', 'export', 'approve', 'manage'], { approve: 'Approve subcontractor prequalification', manage: 'Manage vendor lists and ratings' }],
      ['contact', 'contacts', ['view', 'create', 'update', 'delete', 'export']],
      ['deal', 'deals', CRUD],
      ['pipeline', 'pipelines', ['view', 'manage'], { manage: 'Change pipeline stages' }],
      ['activity', 'notes and calls', ['view', 'create', 'update']],
      ['task', 'tasks', CRUD],
      ['email', 'emails', ['view', 'create', 'update', 'delete'], { create: 'Send emails' }],
      ['template', 'email and proposal templates', CRUD],
      ['report', 'reports', ['view']],
    ],
  ],
  [
    'estimating',
    'Estimates & bids',
    [
      ['estimate', 'estimates', ['view', 'create', 'update', 'delete', 'approve'], { approve: 'Mark estimates accepted' }],
      ['proposal', 'proposals', CRUD],
      ['bid_package', 'bid packages', ['view', 'create', 'update', 'delete', 'approve'], { approve: 'Award bid packages' }],
      ['bid', 'bids', ['view', 'create', 'update']],
    ],
  ],
  [
    'projects',
    'Projects',
    [
      ['project', 'projects', CRUD],
      ['project_member', 'project teams', ['manage'], { manage: 'Add people to projects' }],
      ['schedule', 'schedules', ['view', 'create', 'update']],
      ['document', 'documents', CRUD],
      ['drawing', 'drawings', ['view', 'create', 'update']],
      ['markup', 'drawing markups', ['view', 'create', 'update']],
      ['rfi', 'RFIs', ['view', 'create', 'update']],
      ['submittal', 'submittals', ['view', 'create', 'update', 'approve']],
      ['daily_log', 'daily logs', ['view', 'create', 'update', 'approve']],
      ['photo', 'photos', ['view', 'create', 'update', 'delete']],
      ['punch_item', 'punch list items', ['view', 'create', 'update']],
      ['selection', 'client selections', CRUD],
      ['message', 'project messages', ['view', 'create'], { create: 'Send project messages' }],
    ],
  ],
  [
    'financials',
    'Money',
    [
      ['budget', 'budgets and subcontracts', ['view', 'create', 'update', 'delete', 'approve'], { approve: 'Approve subcontracts and sub pay apps' }],
      ['change_order', 'change orders', ['view', 'create', 'update', 'delete', 'approve']],
      ['invoice', 'invoices', ['view', 'create', 'update', 'delete', 'approve']],
      ['payment', 'payments', ['view', 'create'], { create: 'Record payments' }],
      ['lien_waiver', 'lien waivers', ['view', 'create', 'update']],
    ],
  ],
  [
    'portal',
    'Client portal',
    [['portal', 'the client portal', ['view', 'update', 'manage'], { update: 'Share items with clients', manage: 'Invite clients to the portal' }]],
  ],
  [
    'company',
    'Company settings',
    [
      ['workspace', 'the company workspace', ['view', 'delete'], { view: 'Open the workspace', delete: 'Close the company account' }],
      ['settings', 'company settings', ['manage'], { manage: 'Change company settings' }],
      ['member', 'team members', ['view', 'update', 'manage'], { update: 'Link team members to their companies', manage: 'Invite and manage team members' }],
      ['permission_template', 'roles and permissions', ['manage'], { manage: 'Change roles and permissions' }],
      ['custom_field', 'custom fields', ['view', 'manage']],
      ['audit', 'the activity log', ['view']],
      ['export', 'company data', ['export'], { export: 'Export all company data' }],
      ['billing', 'billing', ['manage'], { manage: 'Manage the plan and billing' }],
      ['integration', 'connected apps', ['view', 'update', 'manage']],
      ['webhook', 'webhooks', ['view', 'manage']],
      ['api_key', 'API keys', ['view', 'manage']],
    ],
  ],
];

export const PERMISSION_CATALOG: readonly CatalogGroup[] = GROUPS.map(([id, label, items]) => ({ id, label, items: items.map(item) }));

/** Every cell on the Roles page, in display order. */
export const CATALOG_CELLS: readonly CatalogCell[] = PERMISSION_CATALOG.flatMap((g) => g.items.flatMap((i) => i.cells));

const CELL_LABELS = new Map(CATALOG_CELLS.map((c) => [`${c.resource}:${c.action}`, c.label]));

/** Plain label for a cell (`deal:update` -> "Edit deals"), falling back to the raw name. */
export function cellLabel(cell: string): string {
  return CELL_LABELS.get(cell) ?? cell;
}

export function isCatalogCell(cell: string): boolean {
  return CELL_LABELS.has(cell);
}

/** One plain line per role, from the owner's side. */
export const ROLE_DESCRIPTIONS: Readonly<Record<Role, string>> = {
  owner: 'Runs the company account. Can do everything, including billing and closing the account.',
  admin: 'Runs the office. Everything an owner can do except billing and closing the account.',
  pm: 'Runs jobs: schedules, documents, change orders, invoices and the client portal.',
  sales: 'Wins work: leads, deals, estimates and proposals.',
  field: 'Works on site: daily logs, photos and punch lists. Sees only the projects they are added to.',
  sub: 'A subcontractor from another company. Sees only the projects they are added to and edits only their own records.',
  client: 'Your customer. Uses the client portal only. Invite clients from Client portal.',
};
